import { createContext, useContext } from "react";
import ServiceContainer from "./ServiceContainer";
import type { RepositoryRegistry } from "./RepositoryRegistry";
import type RBACCatalogRepository from "../administration/repositories/RBACCatalogRepository";

/**
 * React Context for ServiceContainer
 * Provides dependency injection throughout the React component tree
 */
export const ServiceContainerContext = createContext<ServiceContainer | null>(
  null,
);

/**
 * Hook to access the ServiceContainer from React components
 * @throws Error if used outside of ServiceContainerProvider
 */
export const useServiceContainer = (): ServiceContainer => {
  const container = useContext(ServiceContainerContext);

  if (!container) {
    throw new Error(
      "useServiceContainer must be used within a ServiceContainerProvider. " +
        "Make sure to wrap your app with <ServiceContainerProvider>",
    );
  }

  return container;
};

/**
 * Typed access to any registered repository by key. The key argument
 * autocompletes to the RepositoryRegistry key union and the return type is
 * inferred — replaces the ~20 hand-copied `useXRepository` hooks.
 */
export function useRepository<K extends keyof RepositoryRegistry>(
  key: K,
): RepositoryRegistry[K] {
  return useServiceContainer().getRepository(key);
}


/**
 * @deprecated LEGACY — `rbacCatalog` is NOT registered in ServiceContainer (its
 * backend is commented out), so this throws `Repository with key 'rbacCatalog'
 * not found` at runtime today. It is deliberately absent from RepositoryRegistry
 * and quarantined here behind a cast, pending a product decision to either
 * register the backend or drop the RuleFormSheet consumer. Do not add callers —
 * use `useRepository(...)` for real, registered repositories.
 */
export const useRBACCatalogRepository = (): RBACCatalogRepository => {
  const container = useServiceContainer();
  return container.getRepository(
    "rbacCatalog" as keyof RepositoryRegistry,
  ) as unknown as RBACCatalogRepository;
};
